import { FeatureTourStatus, StoredFeatureTourState } from '../types/featureTour';
import store from '@/store';
import { db, FieldValue } from '@/firebase';

// Tour completion is persisted on the user doc (User.featureToursCompletedMap),
// so "have I seen this tour" follows the user across browsers/devices instead
// of being per-browser. localStorage is kept as a transient fallback for the
// case where persistence runs before the user is loaded, and as the source of
// truth for the QA-only force-trigger key.
const STORAGE_PREFIX = 'RD_FEATURE_TOUR_';
const FORCE_TRIGGER_KEY = 'RD_FEATURE_TOUR_FORCE';

const readFromUser = (tourId: string): StoredFeatureTourState | null => {
	const map = (store.state as any).user?.featureToursCompletedMap;
	if (!map) return null;
	const entry = map[tourId];
	return entry ? { ...entry } : null;
};

const readFromLocalStorage = (tourId: string): StoredFeatureTourState | null => {
	const key = `${STORAGE_PREFIX}${tourId}`;
	try {
		const raw = localStorage.getItem(key);
		return raw ? JSON.parse(raw) : null;
	} catch (e) {
		console.error(`[FeatureTour] Failed to read localStorage for ${tourId}`, e);
		return null;
	}
};

const writeToLocalStorage = (tourId: string, state: StoredFeatureTourState): void => {
	try {
		// Drop updatedAt from the localStorage path: Firestore writes use
		// FieldValue.serverTimestamp() (a sentinel that doesn't round-trip
		// through JSON), and no consumer reads updatedAt anyway.
		const { updatedAt, ...rest } = state;
		localStorage.setItem(`${STORAGE_PREFIX}${tourId}`, JSON.stringify(rest));
	} catch (e) {
		console.error(`[FeatureTour] Failed to write localStorage for ${tourId}`, e);
	}
};

// Best-effort write to the user doc. Failures fall back to localStorage so we
// don't lose the value on transient errors — the next successful write will
// migrate it to Firestore. Never throws; the worst case for a missed write is
// the user being re-prompted on next load, which is acceptable.
const writeToUser = async (tourId: string, state: StoredFeatureTourState): Promise<void> => {
	const user = (store.state as any).user;
	if (!user?.id) {
		writeToLocalStorage(tourId, state);
		return;
	}
	try {
		await db.doc(`users/${user.id}`).set(
			{ featureToursCompletedMap: { [tourId]: state } },
			{ merge: true },
		);
		// Successful write — clear the legacy localStorage entry so reads
		// don't drift between sources.
		try { localStorage.removeItem(`${STORAGE_PREFIX}${tourId}`); } catch (e) { /* swallow */ }
	} catch (e) {
		console.error(`[FeatureTour] Failed to persist ${tourId} to user doc`, e);
		writeToLocalStorage(tourId, state);
	}
};

export const featureTourPersistence = {
	isForceTriggered (tourId: string): boolean {
		try {
			const raw = localStorage.getItem(FORCE_TRIGGER_KEY);
			if (!raw) return false;
			return raw.split(',').map((s) => s.trim()).includes(tourId);
		} catch (e) {
			return false;
		}
	},

	// Reads prefer the user doc (server source of truth). Fall back to
	// localStorage for two cases: (1) the user object hasn't loaded yet,
	// (2) the user has a legacy localStorage entry that hasn't been migrated
	// to Firestore yet — the next mark*() call will migrate it.
	getTourState (tourId: string): StoredFeatureTourState | null {
		return readFromUser(tourId) || readFromLocalStorage(tourId);
	},

	saveTourState (tourId: string, partial: Partial<StoredFeatureTourState>): void {
		const existing = this.getTourState(tourId) || {
			status: 'never_seen' as FeatureTourStatus,
			version: 0,
		};

		// FieldValue.serverTimestamp() is a sentinel resolved by Firestore on
		// the server side — monotonic, immune to client clock drift, matches
		// how _created/_updated work elsewhere in the codebase. Cast keeps the
		// StoredFeatureTourState shape intact; the value is consumed by the
		// Firestore SDK during the set() call.
		const updated: StoredFeatureTourState = {
			...existing,
			...partial,
			updatedAt: FieldValue.serverTimestamp() as unknown as StoredFeatureTourState['updatedAt'],
		};

		// Fire-and-forget. Callers don't await — a missed Firestore write
		// degrades to "user gets re-prompted next load", which is acceptable.
		void writeToUser(tourId, updated);
	},

	isTourCompleted (tourId: string, version: number): boolean {
		const state = this.getTourState(tourId);
		return state?.status === 'completed' && state?.version === version;
	},

	// Used by the auto-launch gate. Returns true if the user has been exposed
	// to this tour version in any way — fully completed, dismissed via the X
	// button, or merely started (which could mean they refreshed or navigated
	// away mid-tour). In all of those cases we shouldn't relaunch the tour
	// automatically; the user can replay it manually via the launcher icon.
	hasSeenTour (tourId: string, version: number): boolean {
		const state = this.getTourState(tourId);
		if (!state || state.version !== version) return false;
		return state.status === 'completed'
			|| state.status === 'dismissed'
			|| state.status === 'started';
	},

	markTourStarted (tourId: string, version: number): void {
		this.saveTourState(tourId, { status: 'started', version });
	},

	markTourCompleted (tourId: string, version: number): void {
		this.saveTourState(tourId, { status: 'completed', version });
	},

	markTourDismissed (tourId: string, version: number): void {
		this.saveTourState(tourId, { status: 'dismissed', version });
	},
};
